/* Lounelle PDP purchase UI.
 * Owns selected-option labels and compare-price preview only.
 * Size-guide/low-stock structure is owned by lounelle-low-stock.js.
 * Styling is owned by lounelle-pdp-contract.css.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 767px)');
  var ROOT_SELECTOR = 'product-info';
  var SIZE_FIELDSET_SELECTOR = '.radio__fieldset--sizeguide';
  var SWATCH_FIELDSET_SELECTOR = '.selector-wrapper--swatches .radio__fieldset';
  var PRICE_SELECTOR = '[data-price-wrapper], [data-product-price], [data-compare-price], [data-selected-variant]';
  var rafId = 0;

  function selectedVariant(root) {
    if (!root) return null;
    var source = root.querySelector('[data-selected-variant]');
    if (!source) return null;
    try {
      return JSON.parse(source.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  function mixProductId(productId) {
    var value = String(productId || '');
    var hash = 2166136261;
    for (var index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x85ebca6b);
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 0xc2b2ae35);
    hash ^= hash >>> 16;
    return hash >>> 0;
  }

  function previewDiscountForProduct(productId, priceCents) {
    var minDiscount = 20;
    var maxDiscount = priceCents > 5500 ? 40 : 50;
    return minDiscount + (mixProductId(productId) % (maxDiscount - minDiscount + 1));
  }

  function formatThemeMoney(cents) {
    if (!window.theme || typeof window.theme.formatMoney !== 'function') return '';
    var showCurrencyCode = Boolean(window.theme.settings && window.theme.settings.currency_code_enable);
    var format = showCurrencyCode ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
    if (!format) return '';
    return window.theme.formatMoney(cents, format);
  }

  function applyPreviewComparePrice(root) {
    if (!root) return;

    var variant = selectedVariant(root);
    if (!variant) return;

    var price = Number(variant.price);
    var realComparePrice = Number(variant.compare_at_price || 0);
    if (!Number.isFinite(price) || price <= 0) return;
    if (Number.isFinite(realComparePrice) && realComparePrice > price) return;

    var productId = root.dataset.productId;
    if (!productId) return;

    var discountPercent = previewDiscountForProduct(productId, price);
    var rawComparePrice = (price * 100) / (100 - discountPercent);
    var comparePrice = Math.round((rawComparePrice - 90) / 100) * 100 + 90;
    if (!Number.isFinite(comparePrice) || comparePrice <= price) return;

    var formattedComparePrice = formatThemeMoney(comparePrice);
    if (!formattedComparePrice) return;

    root.querySelectorAll('[data-price-wrapper]').forEach(function (wrapper) {
      var priceNode = wrapper.querySelector('[data-product-price]');
      var compareNode = wrapper.querySelector('[data-compare-price]');
      if (!priceNode || !compareNode) return;

      if (compareNode.textContent !== formattedComparePrice) {
        compareNode.textContent = formattedComparePrice;
      }

      compareNode.style.removeProperty('display');
      compareNode.classList.remove('hidden');
      compareNode.setAttribute('data-lounelle-synthetic-compare', 'true');
      priceNode.classList.add('product__price--sale');
    });
  }

  function selectedValue(fieldset) {
    if (!fieldset) return '';
    var checked = fieldset.querySelector('input[data-single-option-selector]:checked');
    if (checked) return String(checked.value || '').trim();

    var current = fieldset.querySelector('[data-popout-option][aria-current="true"]');
    if (current) return String(current.dataset.value || current.textContent || '').trim();

    return '';
  }

  function ensureSelectedLabel(fieldset) {
    var label = fieldset.querySelector('.radio__legend > .radio__legend__label');
    if (!label) return;

    var value = selectedValue(fieldset);
    var suffix = label.querySelector('.lounelle-option-current');

    if (!value) {
      if (suffix) suffix.remove();
      return;
    }

    if (!suffix) {
      suffix = document.createElement('span');
      suffix.className = 'lounelle-option-current';
      label.appendChild(suffix);
    }

    suffix.textContent = ' : ' + value;
  }

  function setupSizeFieldset(fieldset) {
    if (!fieldset) return;

    ensureSelectedLabel(fieldset);

    var buttons = fieldset.querySelector(':scope > .radio__buttons');
    if (!buttons) return;

    var sizeButtons = buttons.querySelectorAll(':scope > .radio__button');
    fieldset.style.setProperty('--lounelle-size-count', String(Math.max(1, sizeButtons.length)));
    fieldset.classList.toggle('lounelle-size-count-many', sizeButtons.length > 6);
    fieldset.classList.add('lounelle-size-ui-ready');
  }

  function setupSwatchFieldset(fieldset) {
    if (!fieldset || fieldset.matches(SIZE_FIELDSET_SELECTOR)) return;
    ensureSelectedLabel(fieldset);
    fieldset.classList.add('lounelle-swatch-ui-ready');
  }

  function restoreSwatchFieldset(fieldset) {
    if (!fieldset) return;
    var suffix = fieldset.querySelector('.lounelle-option-current');
    if (suffix) suffix.remove();
    fieldset.classList.remove('lounelle-swatch-ui-ready');
  }

  function mutationIsRelevant(mutation) {
    var target = mutation.target && mutation.target.nodeType === 1
      ? mutation.target
      : mutation.target && mutation.target.parentElement;

    if (target && target.closest) {
      if (target.closest(SIZE_FIELDSET_SELECTOR)) return true;
      if (target.closest(PRICE_SELECTOR)) return true;
    }

    var nodes = Array.prototype.slice.call(mutation.addedNodes || [])
      .concat(Array.prototype.slice.call(mutation.removedNodes || []));

    return nodes.some(function (node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.matches && (node.matches(SIZE_FIELDSET_SELECTOR) || node.matches(PRICE_SELECTOR))) return true;
      return Boolean(node.querySelector && (node.querySelector(SIZE_FIELDSET_SELECTOR) || node.querySelector(PRICE_SELECTOR)));
    });
  }

  function ensureRootObserver(root) {
    if (!root || root._lounellePurchaseUiObserver) return;

    var observer = new MutationObserver(function (mutations) {
      if (mutations.some(mutationIsRelevant)) scheduleRoot(root);
    });

    observer.observe(root, { childList: true, subtree: true });
    root._lounellePurchaseUiObserver = observer;
  }

  function applyRoot(root) {
    if (!root || !document.documentElement.contains(root)) return;

    applyPreviewComparePrice(root);
    ensureRootObserver(root);
    root.querySelectorAll(SIZE_FIELDSET_SELECTOR).forEach(setupSizeFieldset);

    if (MOBILE.matches) {
      root.querySelectorAll(SWATCH_FIELDSET_SELECTOR).forEach(setupSwatchFieldset);
    } else {
      root.querySelectorAll(SWATCH_FIELDSET_SELECTOR).forEach(restoreSwatchFieldset);
    }
  }

  function scheduleRoot(root) {
    if (!root) return;
    window.cancelAnimationFrame(rafId);
    rafId = window.requestAnimationFrame(function () {
      applyRoot(root);
    });
  }

  function initAll(scope) {
    if (!document.body || !document.body.classList.contains('template-product')) return;
    var container = scope || document;

    if (container.matches && container.matches(ROOT_SELECTOR)) applyRoot(container);
    container.querySelectorAll(ROOT_SELECTOR).forEach(applyRoot);
  }

  function rootFromTarget(target) {
    return target && target.closest ? target.closest(ROOT_SELECTOR) : null;
  }

  function scheduleVariantPriceRefresh(payload) {
    var sectionId = payload && payload.data && payload.data.sectionId;

    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      var rootSectionId = root.dataset.originalSection || root.dataset.section;
      if (!sectionId || rootSectionId === String(sectionId)) scheduleRoot(root);
    });
  }

  function boot() {
    initAll(document);

    if (
      typeof subscribe === 'function' &&
      window.theme &&
      window.theme.PUB_SUB_EVENTS &&
      window.theme.PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(window.theme.PUB_SUB_EVENTS.variantChange, scheduleVariantPriceRefresh);
    }

    document.addEventListener('change', function (event) {
      var root = rootFromTarget(event.target);
      if (root && event.target.closest('variant-selects')) scheduleRoot(root);
    }, true);

    document.addEventListener('theme:product-info:loaded', function (event) {
      scheduleRoot(rootFromTarget(event.target) || event.target);
    });

    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });

    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', function () {
        initAll(document);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
